// OPTION 2: Dedicated Billing History Page
import React, { useEffect, useState } from "react";
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import { Card, CardBody, Alert, Spinner, Badge } from 'reactstrap';
import axios from "axios";

interface Transaction {
    transaction_label: string;
    transaction_number: number;
    date: string;
    amount: number;
    currency: string;
    status: string;
    payment_method: string;
    invoice_url?: string;
    invoice_pdf?: string;
    period_start: string;
    period_end: string;
}

interface BillingItem {
    purchase_id: number;
    product_title: string;
    product_type: string;
    subscription_status: string;
    started_on: string;
    last_payment_date: string;
    last_payment_amount: number;
    next_billing_date: string | null;
    total_renewals: number;
    total_paid: number;
    failed_renewals_count: number;
    latest_renewal: {
        renewal_number: number;
        amount: number;
        date: string;
        status: string;
    } | null;
}

const BillingHistory = () => {
    const [loading, setLoading] = useState(true);
    const [billingData, setBillingData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [expandedPurchase, setExpandedPurchase] = useState<number | null>(null);
    const [transactionDetails, setTransactionDetails] = useState<any>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    useEffect(() => {
        fetchBillingHistory();
    }, []);

    const fetchBillingHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `/payments/customer/billing-history/`,
                { withCredentials: true }
            );
            
            if (response && response.status === 'SENT') {
                setBillingData(response);
            }
        } catch (err: any) {
            console.error('Error fetching billing history:', err);
            setError(err.response?.data?.error || 'Failed to load billing history');
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactionDetails = async (purchaseId: number) => {
        if (expandedPurchase === purchaseId) {
            setExpandedPurchase(null);
            return;
        }

        try {
            setLoadingDetails(true);
            setExpandedPurchase(purchaseId);
            
            const response = await axios.get(
                `/payments/purchase/${purchaseId}/transactions/`,
                { withCredentials: true }
            );
            
            if (response.data && response.data.status === 'SENT') {
                setTransactionDetails({ ...transactionDetails, [purchaseId]: response.data });
            }
        } catch (err: any) {
            console.error('Error fetching transaction details:', err);
            setError(err.response?.data?.error || 'Failed to load transaction details');
        } finally {
            setLoadingDetails(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status: string) => {
        const statusMap: any = {
            'active': { color: 'success', icon: 'bx-check-circle', text: 'Active' },
            'cancelled': { color: 'danger', icon: 'bx-x-circle', text: 'Cancelled' },
        };
        const s = statusMap[status] || { color: 'secondary', icon: 'bx-info-circle', text: status };
        
        return (
            <Badge color={s.color} className="px-3 py-1">
                <i className={`bx ${s.icon} me-1`}></i>
                {s.text}
            </Badge>
        );
    };

    document.title = "Billing History | BizServe";

    return (
        <div className="page-content">
            {error && (
                <Alert color="danger" toggle={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <div className="loaderContainer" hidden={!loading}>
                <div className="spinner">
                    <div></div><div></div><div></div><div></div><div></div>
                    <div></div><div></div><div></div><div></div><div></div>
                </div>
            </div>

            <div className="container-fluid px-0 px-md-3 py-3" style={{ minHeight: '90vh' }}>
                <Breadcrumbs title="Customer" breadcrumbItem="Billing History" />

                {/* Summary Cards */}
                {billingData && (
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <Card className="rounded-4 border-0 shadow-sm bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <CardBody className="p-4 text-white">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small opacity-75 mb-1">Lifetime Value</div>
                                            <h3 className="fw-bold mb-0">${billingData.summary.lifetime_value?.toFixed(2)}</h3>
                                        </div>
                                        <i className="bx bx-dollar-circle fs-1 opacity-50"></i>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="rounded-4 border-0 shadow-sm bg-gradient" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                                <CardBody className="p-4 text-white">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small opacity-75 mb-1">Active Subscriptions</div>
                                            <h3 className="fw-bold mb-0">{billingData.summary.active_subscriptions}</h3>
                                        </div>
                                        <i className="bx bx-sync fs-1 opacity-50"></i>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="col-md-4">
                            <Card className="rounded-4 border-0 shadow-sm bg-gradient" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                                <CardBody className="p-4 text-white">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="small opacity-75 mb-1">Total Subscriptions</div>
                                            <h3 className="fw-bold mb-0">{billingData.summary.total_subscriptions}</h3>
                                        </div>
                                        <i className="bx bx-package fs-1 opacity-50"></i>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Subscriptions List */}
                <Card className="rounded-4 border-0 shadow-lg bg-white">
                    <CardBody className="p-4">
                        <h5 className="fw-bold mb-4">Your Subscriptions</h5>

                        {billingData?.billing_history?.length === 0 && !loading && (
                            <div className="text-center py-5">
                                <i className="bx bx-receipt fs-1 text-muted mb-3"></i>
                                <div className="text-muted fs-5">No subscription history found.</div>
                            </div>
                        )}

                        {billingData?.billing_history?.map((item: BillingItem, idx: number) => (
                            <div
                                key={item.purchase_id}
                                className="mb-4 p-4 rounded-4 shadow-sm"
                                style={{
                                    background: 'linear-gradient(120deg, #e0e7ff 60%, #f1f5f9 100%)',
                                    border: '1px solid #dbeafe'
                                }}
                            >
                                {/* Header */}
                                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                                    <div>
                                        <h6 className="fw-bold mb-1">{item.product_title}</h6>
                                        <span className="small text-muted">
                                            Started: {formatDate(item.started_on)}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        {getStatusBadge(item.subscription_status)}
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => fetchTransactionDetails(item.purchase_id)}
                                        >
                                            <i className={`bx bx-${expandedPurchase === item.purchase_id ? 'chevron-up' : 'chevron-down'} me-1`}></i>
                                            View Transactions
                                        </button>
                                    </div>
                                </div>

                                {/* Summary */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <div className="small text-muted">Last Payment</div>
                                        <div className="fw-semibold">{formatDate(item.last_payment_date)}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="small text-muted">Last Amount</div>
                                        <div className="fw-semibold text-success">${item.last_payment_amount?.toFixed(2)}</div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="small text-muted">Next Billing</div>
                                        <div className="fw-semibold">
                                            {item.next_billing_date ? formatDate(item.next_billing_date) : 'Cancelled'}
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="small text-muted">Total Paid</div>
                                        <div className="fw-bold text-primary">${item.total_paid?.toFixed(2)}</div>
                                    </div>
                                </div>

                                {/* Expanded Transaction Details */}
                                {expandedPurchase === item.purchase_id && (
                                    <div className="mt-3 p-3 rounded-3 bg-white">
                                        {loadingDetails && (
                                            <div className="text-center py-3">
                                                <Spinner size="sm" color="primary" /> Loading transactions...
                                            </div>
                                        )}

                                        {transactionDetails?.[item.purchase_id] && !loadingDetails && (
                                            <div className="table-responsive">
                                                <table className="table table-sm table-hover mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Transaction</th>
                                                            <th>Amount</th>
                                                            <th>Payment Method</th>
                                                            <th>Status</th>
                                                            <th>Invoice</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {transactionDetails[item.purchase_id].transactions.map((txn: Transaction) => (
                                                            <tr key={txn.transaction_number}>
                                                                <td className="small">{formatDate(txn.date)}</td>
                                                                <td>
                                                                    <span className={`badge ${txn.transaction_number === 0 ? 'bg-primary' : 'bg-secondary'}`}>
                                                                        {txn.transaction_label}
                                                                    </span>
                                                                </td>
                                                                <td className="fw-semibold">${txn.amount.toFixed(2)}</td>
                                                                <td className="small">{txn.payment_method}</td>
                                                                <td>
                                                                    {txn.status === 'succeeded' ? (
                                                                        <span className="badge bg-success">✓ Paid</span>
                                                                    ) : (
                                                                        <span className="badge bg-danger">✗ Failed</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {txn.invoice_url && (
                                                                        <a href={txn.invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-link">
                                                                            <i className="bx bx-download"></i>
                                                                        </a>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <div className="mt-3 text-end">
                                                    <strong>Total: ${transactionDetails[item.purchase_id].summary.total_paid?.toFixed(2)}</strong>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default BillingHistory;

